/**
 * Choisir une commune du référentiel des tables de distances — liste fermée.
 *
 * *Demande de Chris, 08/09/2026 : « il faudrait que la ville du chargement soit un champ de
 * recherche sur la table distance et que le nom de la ville apparaisse donc dans le champ ville,
 * que le champ code postal se remplisse automatiquement en fonction de la table distance […] si
 * la ville n'est pas trouvée il faut la créer avant de pouvoir la sélectionner bien entendu. »*
 *
 * ## Une liste que NOUS dessinons, et pas un champ à liste
 *
 * La ville du chantier (058) utilise un `datalist`, et c'était acceptable là : on y tape le début
 * d'un nom de commune, ce que tous les navigateurs filtrent de la même façon. Ici non — le même
 * défaut que la recherche d'article corrigée ce matin : **le navigateur refiltre les propositions
 * selon sa propre règle**, qui varie d'une version à l'autre, et ce que le serveur trouve
 * n'arrive pas forcément à l'écran. Une liste dessinée n'écarte rien.
 *
 * ## Le code postal ne se saisit pas
 *
 * Il s'affiche à côté du nom, et il vient de la commune choisie. C'est ce que Chris demande, et
 * c'est aussi ce que la mesure a rendu nécessaire : les codes postaux tapés à la main étaient
 * faux — Brocas est 40420 et la saisie disait 40240, Escalans 40310 contre 40130.
 *
 * ## Une ville absente n'est pas une impasse, mais elle ne s'invente pas ici
 *
 * *« Il faut la créer avant de pouvoir la sélectionner […] et paramétrer toutes les distances
 * depuis chaque site. »* L'écran le dit et nomme l'endroit — le menu Distance —, plutôt que
 * d'offrir une création qui laisserait une commune sans aucune distance, donc un km muet sans
 * qu'on sache pourquoi.
 */
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { chercherCommunes } from "./api";
import type { CommuneProposee } from "./api";

/**
 * « chantier » : la ville affichée est celle du chantier, et personne ne l'a choisie ici.
 *
 * Trois mots plutôt qu'une icône : c'est une **origine** de valeur, pas un état, et une pastille
 * de couleur aurait demandé de deviner laquelle. L'infobulle dit la conséquence, qui est la seule
 * chose à retenir — corriger la ville du chantier corrige cette ligne.
 */
function MarqueDuChantier(): JSX.Element {
  return (
    <span
      className="choix-commune__defaut"
      title={
        "Reprise de la ville du chantier. Rien n'est enregistré ici : corriger la ville du " +
        "chantier corrige cette ligne. Choisir une autre ville l'emporte."
      }
    >
      chantier
    </span>
  );
}

/**
 * « hors tables » : la ville affichée a été SAISIE, elle n'est pas au référentiel (073).
 *
 * Le même geste que la mention « chantier » : l'écran dit d'où vient ce qu'il montre. Sans
 * elle, une ville saisie et une ville choisie se liraient pareil, alors que l'une donne un km
 * et l'autre oblige à le saisir — et le champ km, muet, passerait pour une panne.
 */
function MarqueHorsTables(): JSX.Element {
  return (
    <span
      className="choix-commune__libre"
      title={
        "Ville saisie à la main : elle n'est pas au référentiel des tables de distances, " +
        "donc aucune distance ne s'en déduit. Le kilométrage se saisit dans le champ km."
      }
    >
      hors tables
    </span>
  );
}

export function ChoixCommune({
  libelle,
  communeId,
  nom,
  codePostal,
  parDefaut = false,
  libelleRetrait = "Retirer la ville",
  villeLibre = null,
  onSaisirLibre,
  modifiable,
  onChoisir,
}: {
  readonly libelle: string;
  readonly communeId: string | null;
  /** Le nom déduit du référentiel, pour afficher sans recharger. */
  readonly nom: string | null;
  readonly codePostal: string | null;
  /**
   * La ville SAISIE, quand elle n'est pas au référentiel des distances (073).
   *
   * *« Il faut pouvoir entrer dans ville de chargement un nom de ville qui n'existe pas dans
   * nos tables distance »* (Chris, 08/09/2026).* Exclusive de `communeId` : le serveur ne
   * garde qu'une des deux, et la base l'impose.
   */
  readonly villeLibre?: string | null;
  /**
   * Ce que la saisie libre enregistre. **Absent = le champ reste une liste fermée** — c'est le
   * cas de la ville de LIVRAISON, qui est un de nos chantiers, donc une commune qu'on connaît.
   */
  readonly onSaisirLibre?: (nom: string | null) => void;
  /**
   * Vrai quand la commune affichée est un DÉFAUT et non un choix — la ville du chantier, reprise
   * sur la ville de livraison (PRD v1.21).
   *
   * Elle s'affiche comme une valeur, parce que c'en est une : le kilométrage s'en déduit et le
   * conducteur n'a rien à saisir. Mais l'écran le **dit**, et n'offre pas de la retirer — rien
   * n'est enregistré sur ce porteur, et un bouton qui n'efface rien passe pour un bouton cassé.
   */
  readonly parDefaut?: boolean;
  /** Ce que le retrait annonce. Sur un champ qui a un défaut, il y REVIENT plutôt qu'il n'efface. */
  readonly libelleRetrait?: string;
  readonly modifiable: boolean;
  readonly onChoisir: (communeId: string | null) => void;
}): JSX.Element {
  const [cherche, setCherche] = useState("");
  const [trouvees, setTrouvees] = useState<readonly CommuneProposee[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const bloc = useRef<HTMLDivElement>(null);

  // La recherche part après un temps d'arrêt, et le résultat en retard est jeté : taper vite
  // ferait sinon arriver les réponses dans un ordre quelconque, et la liste afficherait celle
  // d'une frappe précédente.
  useEffect(() => {
    const terme = cherche.trim();
    if (terme.length < 2) {
      setTrouvees([]);
      return;
    }
    let abandonne = false;
    const minuteur = window.setTimeout(() => {
      chercherCommunes(terme)
        .then((r) => {
          if (!abandonne) setTrouvees(r.communes);
        })
        .catch(() => {
          if (!abandonne) setTrouvees([]);
        });
    }, 250);
    return () => {
      abandonne = true;
      window.clearTimeout(minuteur);
    };
  }, [cherche]);

  // Un clic ailleurs referme la liste : sans cela, deux panneaux ouverts se recouvriraient.
  useEffect(() => {
    if (!ouvert) return;
    const dehors = (e: MouseEvent): void => {
      if (bloc.current !== null && !bloc.current.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener("mousedown", dehors);
    return () => {
      document.removeEventListener("mousedown", dehors);
    };
  }, [ouvert]);

  /*
    DEUX CHAMPS, et c'est ce que Chris a demandé.

    *« Que le nom de la ville apparaisse donc dans le champ ville, que le champ code postal
    ville de chargement se remplisse automatiquement en fonction de la table distance. »* Le
    code postal est donc un champ à part, à côté du nom — et non une parenthèse à l'intérieur.

    Ce n'est pas cosmétique : la première version les collait dans un seul champ, et
    « LABASTIDE-D'ARMAGNAC (40240) » s'y coupait à « LABASTIDE-D'ARMAGNAC (4 » sur la carte
    d'une pastille. Le code postal, la seule chose qui distingue 76 noms qui se répètent,
    était précisément la moitié qui disparaissait.
  */
  // La ville saisie s'affiche à la place de celle du référentiel : le serveur n'en garde
  // qu'une, et les deux ne peuvent donc pas être là en même temps.
  const affiche = nom ?? villeLibre ?? "";

  if (!modifiable) {
    return (
      <span className="choix-commune choix-commune--figee" title={libelle}>
        {affiche === "" ? "—" : affiche}
        {codePostal !== null && <span className="choix-commune__cp">{codePostal}</span>}
        {parDefaut && <MarqueDuChantier />}
        {villeLibre !== null && <MarqueHorsTables />}
      </span>
    );
  }

  return (
    <div className="choix-commune" ref={bloc}>
      <input
        /*
          PAS DE LISERÉ « À REMPLIR », et c'est voulu.

          Les deux villes restent FACULTATIVES — « la ville et le code postal de la ville de
          provenance SI ON LA CONNAÎT » (métier, 13/08/2026), et Chris n'a pas dit le
          contraire en les rattachant au référentiel. Les marquer en ambre réclamerait une
          réponse sur chaque ligne de matériau d'un chantier, y compris celles dont personne
          ne sait encore d'où le camion partira.

          Ce qui manque se dit à un seul endroit : le champ km, qui explique « choisir les
          deux villes pour obtenir la distance ». Deux signaux pour la même absence finiraient
          par ne plus dire la même chose.
        */
        className="saisie"
        value={ouvert ? cherche : affiche}
        placeholder={libelle}
        title={libelle}
        onFocus={() => {
          setOuvert(true);
          setCherche("");
        }}
        onChange={(e) => {
          setCherche(e.target.value);
          setOuvert(true);
        }}
      />

      {/* Le code postal ne se saisit pas : il vient de la commune choisie. Affiché même
          pendant la recherche — il désigne encore la commune rattachée, celle qu'on
          remplacera peut-être, et le faire disparaître à la frappe ferait croire à un
          effacement. */}
      {codePostal !== null && <span className="choix-commune__cp">{codePostal}</span>}

      {/* Pendant la recherche, les marques disparaissent : le champ ne montre plus la commune
          rattachée mais ce qu'on tape, et « chantier » désignerait alors la mauvaise chose. */}
      {parDefaut && !ouvert && <MarqueDuChantier />}
      {villeLibre !== null && !ouvert && <MarqueHorsTables />}

      {ouvert && (
        <ul className="choix-commune__trouvees">
          {trouvees.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => {
                  onChoisir(c.id);
                  setOuvert(false);
                  setCherche("");
                }}
              >
                <strong>{c.nom}</strong>
                {/* Le code postal DISTINGUE : 76 noms se répètent au référentiel, jusqu'à
                    quatre fois. Sans lui, choisir dans la liste serait un tirage au sort. */}
                <span>{c.codePostal}</span>
                {c.codeDepartement !== null && (
                  <span className="choix-commune__dep">dép. {c.codeDepartement}</span>
                )}
              </button>
            </li>
          ))}

          {cherche.trim().length >= 2 && trouvees.length === 0 && onSaisirLibre === undefined && (
            <li className="choix-commune__absente">
              Aucune commune. Une ville absente se crée au menu <strong>Distance</strong>, avec
              ses distances depuis chaque site — sans quoi son kilométrage resterait muet.
            </li>
          )}

          {/*
            GARDER CE QU'ON TAPE, quand le champ l'autorise (073).

            Proposé **même quand des communes sont trouvées**, et en bas de liste : le
            référentiel peut contenir un homonyme sans que ce soit le bon endroit — une zone
            industrielle, un lieu-dit, un dépôt de fournisseur. Le forcer à choisir dans la
            liste dès qu'elle n'est pas vide rendrait la saisie libre inatteignable dans le
            cas le plus courant.

            En bas et pas en haut : une commune du référentiel reste le bon choix quand elle
            convient, puisqu'elle donne le kilométrage.
          */}
          {onSaisirLibre !== undefined && cherche.trim().length >= 2 && (
            <li className="choix-commune__hors-tables">
              <button
                type="button"
                title={
                  "Garde ce nom tel quel. Aucune distance ne s'en déduit — les tables ne " +
                  "portent que les communes du référentiel — donc le km se saisit à la main."
                }
                onClick={() => {
                  onSaisirLibre(cherche.trim());
                  setOuvert(false);
                  setCherche("");
                }}
              >
                Garder « {cherche.trim()} » — hors tables de distances
              </button>
            </li>
          )}

          {cherche.trim().length < 2 && (
            <li className="choix-commune__invite">Taper au moins deux lettres.</li>
          )}

          {/* Retirer la ville est une réponse — « je ne sais pas encore d'où » — et non une
              absence de geste. Elle n'est offerte que s'il y a quelque chose à retirer : une
              commune CHOISIE. Une commune reprise du chantier (PRD v1.21) n'est enregistrée nulle
              part, et le bouton n'aurait rien à effacer. */}
          {((communeId !== null && !parDefaut) || villeLibre !== null) && (
            <li>
              <button
                type="button"
                className="choix-commune__retirer"
                title={
                  libelleRetrait === "Retirer la ville"
                    ? "Le champ redevient vide."
                    : "Efface le choix : la ville du chantier reprend sa place, ou le champ " +
                      "reste vide si le chantier n'a pas de ville."
                }
                onClick={() => {
                  // Retirer une ville SAISIE passe par le chemin de la saisie libre : le
                  // serveur ne verrait pas la différence sur `communeProvenanceId`, qui est
                  // déjà nul dans ce cas — la demande n'effacerait rien.
                  if (villeLibre !== null && onSaisirLibre !== undefined) onSaisirLibre(null);
                  else onChoisir(null);
                  setOuvert(false);
                  setCherche("");
                }}
              >
                {libelleRetrait}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
